import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../utils/authContext";
import { analyticsUrl, getRouteAnalyticsMetadata, isAnalyticsExcluded, initializeAnalytics, logPageView, trackEvent } from "../utils/analytics";
import { CONSENT_CHANGED_EVENT, hasAnalyticsConsent } from "../utils/consent";

const clock = () => performance.now();
const publicCategories = new Set(["marketing", "conversion", "content", "support", "legal"]);
const safeLabel = (value) => {
  const label = String(value || "").trim().replace(/\s+/g, " ").slice(0, 100);
  return /@|https?:|\d{7}/i.test(label) ? "" : label;
};

export default function AnalyticsTracker() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const userRef = useRef(user);
  userRef.current = user;
  const previousUrl = useRef(null);
  const [consented, setConsented] = useState(hasAnalyticsConsent);

  useEffect(() => {
    const sync = () => {
      const allowed = hasAnalyticsConsent() && !isAnalyticsExcluded();
      window.gtag?.("consent", "update", { analytics_storage: allowed ? "granted" : "denied" });
      setConsented(allowed);
    };
    window.addEventListener(CONSENT_CHANGED_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CONSENT_CHANGED_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  useEffect(() => {
    if (!consented) return undefined;
    // Deferring startup avoids counting React StrictMode's probe mount twice.
    let dispose = () => {};
    const timer = window.setTimeout(() => {
      if (!initializeAnalytics()) return;
      const metadata = getRouteAnalyticsMetadata(pathname);
      const payload = logPageView({ pathname, title: document.title, metadata, user: userRef.current,
        referrer: previousUrl.current ?? document.referrer });
      if (!payload) return;
      previousUrl.current = payload.page_location;
      if (pathname === "/pricing") trackEvent("pricing_view", { funnel_name: "retailer_acquisition" });
      if (pathname === "/signup" || pathname.startsWith("/signup/")) trackEvent("signup_view", { funnel_name: "signup" });

      const startedAt = clock();
      let visibleSince = document.visibilityState === "hidden" ? null : startedAt;
      let activeMs = 0;
      let reportedMs = 0;
      let maxScroll = 0;
      let exited = false;
      let raf = 0;
      const scrolls = new Set();
      const sections = new Set();
      const context = () => ({ ...payload, active_time_msec: Math.round(activeMs), max_scroll_depth: maxScroll });
      const accumulate = () => {
        if (visibleSince !== null) activeMs += clock() - visibleSince;
        visibleSince = document.visibilityState === "hidden" ? null : clock();
      };
      const engagement = () => {
        accumulate();
        const delta = activeMs - reportedMs;
        if (delta < 1) return;
        // GA owns engagement_time_msec; our foreground timer is a separate metric.
        trackEvent("page_engagement", { ...payload, active_time_msec: Math.round(delta) });
        reportedMs = activeMs;
      };
      const exit = (reason) => {
        if (exited) return;
        engagement();
        trackEvent("page_exit", { ...context(), exit_reason: reason, elapsed_time_msec: Math.round(clock() - startedAt) });
        exited = true;
        visibleSince = null;
      };
      const onVisibility = () => { engagement(); };
      const onPageHide = (event) => { exit(event.persisted ? "page_suspended" : "page_hidden"); };
      const onPageShow = () => { exited = false; visibleSince = clock(); };
      const onScroll = () => {
        if (raf) return;
        raf = window.requestAnimationFrame(() => {
          raf = 0;
          const doc = document.documentElement;
          const height = Math.max(doc.scrollHeight, document.body?.scrollHeight || 0);
          const available = height - window.innerHeight;
          // A page that fits in the viewport wasn't scrolled.
          if (available <= 0) return;
          maxScroll = Math.max(maxScroll, Math.min(100, Math.round((window.scrollY / available) * 100)));
          [25, 50, 75, 90].forEach((depth) => {
            if (maxScroll >= depth && !scrolls.has(depth)) {
              scrolls.add(depth);
              trackEvent("scroll_depth", { ...payload, scroll_depth: depth });
            }
          });
        });
      };
      const onClick = (event) => {
        const target = event.target?.closest?.("a,button,[role='button'],[role='tab'],summary,[data-analytics-click]");
        if (!target || target.closest("[data-analytics-ignore]")) return;
        const section = target.closest("[data-analytics-section],section,nav,header,footer,[role='dialog']");
        const isPublic = publicCategories.has(metadata.page_category) && !userRef.current;
        const sectionId = section?.dataset.analyticsSection || section?.id || section?.tagName.toLowerCase() || "page";
        const label = target.dataset.analyticsClick || (isPublic && !target.closest("form,[role='dialog']")
          ? safeLabel(target.getAttribute("aria-label") || target.textContent) : target.getAttribute("type") || target.tagName.toLowerCase());
        let destination = "";
        let host = "";
        let contactType = "";
        let planId;
        let leadType;
        const href = target.getAttribute("href");
        if (href && /^(mailto:|tel:)/.test(href)) contactType = href.split(":")[0];
        else if (href && !/^(#|javascript:)/i.test(href)) {
          const link = new URL(href, window.location.origin);
          const plan = (link.searchParams.get("plan") || "").toLowerCase();
          const journey = link.searchParams.get("journey");
          if (/^[a-z][a-z0-9_-]{0,60}$/.test(plan)) planId = plan;
          if (["business", "enterprise", "whitelabel", "demo", "pricing"].includes(journey)) leadType = journey;
          destination = analyticsUrl(href);
          if (destination) host = new URL(destination).host;
        }
        const click = { ...payload, click_name: label, click_section: sectionId, element_type: target.tagName.toLowerCase(),
          destination_path: destination ? new URL(destination).pathname : "", destination_host: host,
          plan_id: planId, lead_type: leadType,
          billing_cycle: target.id === "bAnnual" ? "year" : target.id === "bMonthly" ? "month" :
            document.getElementById("bAnnual")?.getAttribute("aria-pressed") === "true" ? "year" : undefined };
        trackEvent("site_click", click);
        if (["bMonthly", "bAnnual"].includes(target.id)) trackEvent("pricing_billing_cycle_select", click);
        if (host === window.location.host && click.destination_path === "/contact") {
          trackEvent("commercial_cta_click", click);
          if (planId) trackEvent("pricing_plan_select", click);
        }
        if (contactType) trackEvent("contact_click", { ...payload, contact_type: contactType });
        if (host && host !== window.location.host) trackEvent("outbound_click", { ...payload, outbound_url: destination, outbound_host: host });
        if (host === window.location.host && /^\/pilot(?:\/|$)|^\/partners\/pilot-apply$/.test(click.destination_path)) {
          trackEvent("pilot_cta_click", click);
        }
        if (host === window.location.host && /^\/signup(?:\/|$)/.test(click.destination_path)) trackEvent("signup_cta_click", click);
      };

      // Section visibility reveals what visitors actually reached, rather than just the route.
      // Use public headings as stable fallbacks; never read authenticated content or form values.
      let observer;
      let mutations;
      const observed = new WeakSet();
      const observeSections = () => {
        document.querySelectorAll("[data-analytics-section],main section,section").forEach((section, index) => {
          if (observed.has(section) || section.closest("form,[role='dialog'],[data-analytics-ignore]")) return;
          const sectionId = section.dataset.analyticsSection || section.id || section.querySelector("[id]")?.id || `section_${index + 1}`;
          section.dataset.analyticsObservedId = sectionId;
          observed.add(section);
          observer.observe(section);
        });
      };
      if (typeof IntersectionObserver !== "undefined" && publicCategories.has(metadata.page_category) && !userRef.current) {
        observer = new IntersectionObserver((entries) => entries.forEach(({ target, isIntersecting }) => {
          const id = target.dataset.analyticsObservedId;
          if (!isIntersecting || sections.has(id) || document.visibilityState === "hidden") return;
          sections.add(id);
          trackEvent("section_view", { ...payload, section_id: id,
            section_name: safeLabel(target.querySelector("h1,h2,h3")?.textContent) });
        }), { threshold: 0, rootMargin: "-10% 0px -10% 0px" });
        observeSections();
        mutations = new MutationObserver(observeSections);
        mutations.observe(document.body, { childList: true, subtree: true });
      }
      const heartbeat = window.setInterval(engagement, 10000);
      document.addEventListener("visibilitychange", onVisibility);
      window.addEventListener("pagehide", onPageHide);
      window.addEventListener("pageshow", onPageShow);
      window.addEventListener("scroll", onScroll, { passive: true });
      // Capture handles buttons which stop propagation, including pricing plan CTAs.
      document.addEventListener("click", onClick, true);
      dispose = () => {
        exit("route_change");
        window.clearInterval(heartbeat);
        if (raf) window.cancelAnimationFrame(raf);
        observer?.disconnect();
        mutations?.disconnect();
        document.removeEventListener("visibilitychange", onVisibility);
        window.removeEventListener("pagehide", onPageHide);
        window.removeEventListener("pageshow", onPageShow);
        window.removeEventListener("scroll", onScroll);
        document.removeEventListener("click", onClick, true);
      };
    }, 0);
    return () => { window.clearTimeout(timer); dispose(); };
  }, [pathname, consented]);

  return null;
}
